import React, { useCallback, useEffect, useRef, useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import CutOutOverlay from '@pkg/components/OnboardingTutorial/CutOutOverlay';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { useMediaQuery } from '@pkg/providers';

import { useLockBodyScroll } from '@app/helpers';

import { Typography } from '../DesignSystem';

import {
    OnboardingButtonContainer,
    OnboardingContent,
    OnboardingImage,
    OnboardingSkipButton,
    OnboardingSlide,
    OnboardingSliderDot,
    OnboardingSliderDots,
    OnboardingTitle,
    OnboardingWrapper,
} from './styles';

interface OnboardingSlide {
    imageURL?: string;
    title?: string;
    content?: string;
    button?: string;
}
interface OnboardingTutorialProps {
    slides: OnboardingSlide[];
    refs?: React.RefObject<HTMLDivElement>[];
    size?: 'small' | 'medium' | 'large';
    highlightElement: string;
    onClose: () => void;
    onSkip?: () => void;
    skipButtonLabel?: string;
    useLockScroll?: boolean;
    onSlideChange?: (index: number) => void;
}

const OnboardingTutorial = ({
    slides,
    onClose,
    onSkip,
    skipButtonLabel,
    size = 'medium',
    highlightElement,
    useLockScroll = false,
    onSlideChange,
}: OnboardingTutorialProps) => {
    useLockBodyScroll(useLockScroll);
    const wrapper = useRef<HTMLDivElement>(null);
    const { tablet } = useMediaQuery();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [previousSlide, setPreviousSlide] = useState(0);

    const onUserClose = useCallback(() => {
        if (onSkip) onSkip();
        else onClose();
    }, [onClose, onSkip]);

    useEffect(() => {
        function handleKeyDown(event) {
            if (event.key === 'Escape') {
                onUserClose();
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onUserClose]);

    const switchSlide = (index) => {
        if (index >= 0 && index <= slides.length - 1) {
            setPreviousSlide(currentSlide);
            setCurrentSlide(index);
            onSlideChange && onSlideChange(index);
        }
    };

    const setPosition = useCallback(async () => {
        const el = document.querySelector(`.${highlightElement}`);
        const box = el?.getBoundingClientRect();

        if (wrapper.current && box) {
            wrapper.current.style.top = `${Math.min(
                Math.max(box.top + box.height * 0.5, 120),
                window.innerHeight - 100,
            )}px`;
            if (box.left < window.innerWidth * 0.5) {
                wrapper.current.style.left = `${box.right + 20}px`;
                wrapper.current.style.transform = `translate3d(0, -50%, 0)`;
            } else {
                wrapper.current.style.left = `${box.left - 20}px`;
                wrapper.current.style.transform = `translate3d(-100%, -50%, 0)`;
            }
        }
    }, [highlightElement]);

    useEffect(() => {
        return () => {
            window.removeEventListener('resize', setPosition);
            window.removeEventListener('scroll', setPosition);
        };
    }, [setPosition]);

    useEffect(() => {
        window.addEventListener('resize', setPosition);
        window.addEventListener('scroll', setPosition);
        void setPosition();
    }, [highlightElement, setPosition, wrapper]);

    const direction = previousSlide < currentSlide ? 1 : -1;

    return (
        <>
            <AnimatePresence>
                <CutOutOverlay highlightElement={highlightElement} onClose={onUserClose} />
                <OnboardingWrapper
                    $size={size}
                    ref={wrapper}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 0.4, delay: 0.6 } }}
                    exit={{ opacity: 0, transition: { duration: 0.5 } }}
                >
                    <AnimatePresence mode="wait">
                        <OnboardingSlide
                            initial={{ opacity: 0, x: 20 * direction }}
                            animate={{ opacity: 1, x: 0, transition: { duration: 0.5 } }}
                            exit={{ opacity: 0, x: 20 * direction, transition: { duration: 0.3 } }}
                            key={`onboarding-slide-${currentSlide}`}
                        >
                            {slides[currentSlide].imageURL && <OnboardingImage src={slides[currentSlide].imageURL} />}
                            {slides[currentSlide].title && (
                                <OnboardingTitle>
                                    <Typography
                                        size={tablet ? 24 : 20}
                                        type="headline"
                                        variant="span"
                                        align="left"
                                        weight="700"
                                    >
                                        {slides[currentSlide].title}
                                    </Typography>
                                </OnboardingTitle>
                            )}
                            {slides[currentSlide].content && (
                                <OnboardingContent>
                                    <Typography
                                        size={tablet ? 18 : 16}
                                        type="paragraph"
                                        color="#c4c4c4"
                                        align="left"
                                        weight="400"
                                    >
                                        {slides[currentSlide].content}
                                    </Typography>
                                </OnboardingContent>
                            )}
                            <OnboardingButtonContainer>
                                <Button
                                    size={tablet ? 'medium' : 'large'}
                                    fluid={false}
                                    onClick={
                                        currentSlide === slides.length - 1
                                            ? onClose
                                            : () => switchSlide(currentSlide + 1)
                                    }
                                    style={{ width: 'fit-content' }}
                                >
                                    {slides[currentSlide].button || 'Got it!'}
                                </Button>
                                {onSkip && currentSlide === slides.length - 1 && (
                                    <OnboardingSkipButton onClick={onSkip}>{skipButtonLabel}</OnboardingSkipButton>
                                )}
                            </OnboardingButtonContainer>
                        </OnboardingSlide>
                    </AnimatePresence>
                    {slides.length > 1 && (
                        <OnboardingSliderDots>
                            {slides.map((slide, index) => (
                                <OnboardingSliderDot
                                    key={`${slide.title}-${slide.content}-${index}`}
                                    active={index === currentSlide}
                                    onClick={() => switchSlide(index)}
                                />
                            ))}
                        </OnboardingSliderDots>
                    )}
                </OnboardingWrapper>
            </AnimatePresence>
        </>
    );
};

export default OnboardingTutorial;
